import React from 'react';
import { Lock, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import EmployeePicker from './EmployeePicker';
import LinkedEntityPicker from './LinkedEntityPicker';

/**
 * Shared task form fields used by:
 *   - CRM "Add Task" modal
 *   - Lead/Opportunity/Deal detail drawer quick-log
 *   - Kanban Task Board create modal
 *   - HR Onboarding tasks
 *
 * Rules:
 *  - Owner = current user (creator), greyed out / read-only.
 *    Only `super_admin` and MD (department='MD') can override.
 *  - Assigned To = separate EmployeePicker.
 *  - Due Date = HTML5 datetime-local (date + time, 24h).
 *  - Link To = department-aware LinkedEntityPicker (omitted when `hideLinkedEntity` prop set,
 *              e.g. when the task is already in the context of a specific lead/opp drawer).
 *
 * Props:
 *   value     { description, due_at, owner, assigned_to, linked_entity }
 *   onChange  (next) => void  – merges into form state
 *   hideLinkedEntity  bool    – hide the linked-entity row (when context already implies it)
 *   descriptionLabel  string  – override label
 *   testIdPrefix      string  – prefix for data-testids
 */
export default function TaskFormFields({
  value,
  onChange,
  hideLinkedEntity = false,
  descriptionLabel = 'Description',
  testIdPrefix = 'task',
}) {
  const { user } = useAuth() || {};
  const currentUserName = user?.name || user?.email || '';
  const canOverrideOwner = user?.role === 'super_admin' || user?.department === 'MD';

  // Ensure owner defaults to current user once
  React.useEffect(() => {
    if (!value.owner && currentUserName) {
      onChange({ ...value, owner: currentUserName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserName]);

  const update = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      {/* Description */}
      <div>
        <label className="label">{descriptionLabel} *</label>
        <input
          type="text"
          value={value.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          className="input"
          placeholder="e.g., Follow up on quotation"
          required
          data-testid={`${testIdPrefix}-description-input`}
        />
      </div>

      {/* Due date + time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Due Date &amp; Time *</label>
          <input
            type="datetime-local"
            value={value.due_at || ''}
            onChange={(e) => update({ due_at: e.target.value })}
            className="input"
            required
            data-testid={`${testIdPrefix}-due-input`}
          />
        </div>

        {/* Owner — read-only chip for normal users, editable for super_admin/MD */}
        <div>
          <label className="label flex items-center gap-1">
            Owner
            {!canOverrideOwner && <Lock size={11} className="text-slate-400" />}
          </label>
          {canOverrideOwner ? (
            <EmployeePicker
              value={value.owner || currentUserName}
              onChange={(v) => update({ owner: v })}
              testid={`${testIdPrefix}-owner-picker`}
            />
          ) : (
            <div
              className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-sm text-slate-600 cursor-not-allowed"
              title="The owner is automatically set to you (the creator). Only super-admin / MD can override."
              data-testid={`${testIdPrefix}-owner-readonly`}
            >
              <UserIcon size={13} className="text-slate-400" />
              <span className="truncate">{currentUserName || '—'}</span>
              <span className="ml-auto text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-400">You</span>
            </div>
          )}
        </div>
      </div>

      {/* Assigned To */}
      <div>
        <label className="label">Assigned To</label>
        <EmployeePicker
          value={value.assigned_to || ''}
          onChange={(v) => update({ assigned_to: v })}
          placeholder="Search team member to assign…"
          testid={`${testIdPrefix}-assigned-picker`}
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Leave blank to keep this task with you.
        </p>
      </div>

      {/* Link To */}
      {!hideLinkedEntity && (
        <div>
          <label className="label">Link to record</label>
          <LinkedEntityPicker
            value={value.linked_entity || null}
            onChange={(sel) => update({ linked_entity: sel })}
            department={user?.department}
            role={user?.role}
            testid={`${testIdPrefix}-link-picker`}
          />
        </div>
      )}
    </div>
  );
}
